import { forwardRef, useEffect, useId, useState } from 'react';
import type { WithClassName } from './types';

export type TimeProps = WithClassName & {
  value: string | Date | null;
  timeZone?: string;
  locale?: string;
  missingLabel?: string;
};
type Parsed = { date: Date; calendar: boolean; iso: string };

function calendarDate(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().startsWith(value) ? date : undefined;
}

function parseTime(value: TimeProps['value']): Parsed | undefined {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? { date: value, calendar: false, iso: value.toISOString() } : undefined;
  }
  if (!value) return undefined;
  const calendar = calendarDate(value);
  if (calendar) return { date: calendar, calendar: true, iso: value };
  // API instants must specify a zone. Reject calendar rollovers as well.
  const instant = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/i.exec(value);
  if (!instant || !calendarDate(instant[1]!) || Number(instant[2]) > 23
    || Number(instant[3]) > 59 || Number(instant[4] ?? 0) > 59) return undefined;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? { date, calendar: false, iso: value } : undefined;
}

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** Calendar day in the chosen zone, including around DST and local evenings. */
export function formatLocalDate(value: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-iso8601-nu-latn', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(value);
  const part = (name: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === name)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function absolute(parsed: Parsed, props: TimeProps, seconds = false, format = 'datetime') {
  const timeZone = parsed.calendar ? 'UTC' : props.timeZone ?? browserZone();
  const options: Intl.DateTimeFormatOptions = parsed.calendar || format === 'date'
    ? { ...dateOptions, timeZone }
    : format === 'time' ? { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      ...(seconds ? { second: '2-digit' as const } : {}) }
    : { ...dateOptions, timeZone, hour: '2-digit', minute: '2-digit',
      ...(seconds ? { second: '2-digit' as const } : {}), timeZoneName: 'short' };
  return new Intl.DateTimeFormat(props.locale, options).format(parsed.date);
}

function fullAbsolute(parsed: Parsed, props: TimeProps) {
  if (parsed.calendar) return absolute(parsed, props);
  const timeZone = props.timeZone ?? browserZone();
  const offset = new Intl.DateTimeFormat(props.locale, {
    timeZone, timeZoneName: 'longOffset' as Intl.DateTimeFormatOptions['timeZoneName'],
  }).formatToParts(parsed.date).find((part) => part.type === 'timeZoneName')?.value;
  return `${absolute(parsed, props, true)} · ${timeZone} · ${offset ?? 'UTC'}`;
}

function display(props: TimeProps, relativeNow?: Date, seconds = false, format = 'datetime') {
  const parsed = parseTime(props.value);
  if (!parsed) return {
    text: props.value == null || props.value === '' ? props.missingLabel ?? 'Not recorded' : 'Time unavailable',
  };
  try {
    let text = absolute(parsed, props, seconds, format);
    if (relativeNow && !parsed.calendar) {
      const delta = (parsed.date.getTime() - relativeNow.getTime()) / 1000;
      if (!Number.isFinite(delta)) return { text: 'Time unavailable' };
      const distance = Math.abs(delta);
      if (distance < 60) text = delta > 0 ? 'In less than a minute' : 'Just now';
      else if (distance < 7 * 86400) {
        const [unit, divisor] = distance < 3600 ? ['minute', 60] as const
          : distance < 86400 ? ['hour', 3600] as const : ['day', 86400] as const;
        text = new Intl.RelativeTimeFormat(props.locale, { numeric: 'always' })
          .format(Math.trunc(delta / divisor), unit);
      } else text = absolute(parsed, props, false, 'date');
    }
    return { text, iso: parsed.iso, full: fullAbsolute(parsed, props) };
  } catch {
    return { text: 'Time unavailable' };
  }
}

type TimeDisplayProps = ReturnType<typeof display> & WithClassName;
const TimeDisplay = forwardRef<HTMLTimeElement, TimeDisplayProps>(function TimeDisplay(
  { text, iso, full, className }, ref,
) {
  const id = useId();
  const [dismissed, setDismissed] = useState(false);
  return <span className={`ui-time ${className ?? ''}`} data-tooltip-dismissed={dismissed || undefined}
    onMouseEnter={() => setDismissed(false)} onFocus={() => setDismissed(false)}
    onKeyDown={(event) => {
      if (event.key === 'Escape' && full) { setDismissed(true); event.stopPropagation(); }
    }}>
    <time ref={ref} dateTime={iso} tabIndex={full ? 0 : undefined} aria-label={full ? `${text}; ${full}` : undefined}
      aria-describedby={full ? id : undefined}>{text}</time>
    {full && <span className="ui-time__tooltip" id={id} role="tooltip">{full}</span>}
  </span>;
});

export type DateTimeProps = TimeProps & { format?: 'date' | 'datetime' | 'time'; seconds?: boolean };
export const DateTime = forwardRef<HTMLTimeElement, DateTimeProps>(function DateTime(props, ref) {
  return <TimeDisplay {...display(props, undefined, props.seconds, props.format)} className={props.className} ref={ref} />;
});

export const RelativeTime = forwardRef<HTMLTimeElement, TimeProps & { now?: Date }>(function RelativeTime(props, ref) {
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    if (props.now) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function schedule() {
      if (timer) clearTimeout(timer);
      if (document.visibilityState === 'hidden') return;
      setClock(new Date());
      timer = setTimeout(schedule, 60000 - Date.now() % 60000);
    }
    schedule();
    document.addEventListener('visibilitychange', schedule);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [props.now]);
  return <TimeDisplay {...display(props, props.now ?? clock)} className={props.className} ref={ref} />;
});
